import React, { useState, useMemo } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { LEGAL_URLS } from '../../config/legalUrls';
import {
  HelpCircle,
  ShieldCheck,
  CreditCard,
  BookOpen,
  LifeBuoy,
  CheckCircle,
  Lock,
  ChevronDown,
  Mail,
  ExternalLink,
  Info,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { useSubscriptionTier } from '../../hooks/useSubscriptionTier';
import {
  ROLE_PERMISSIONS,
  ROLE_DESCRIPTIONS,
  FAQ_ENTRIES,
  GUIDES,
  SUPPORT_CHANNELS,
} from '../../config/helpContent';
import type { AppRole, Permission } from '../../config/helpContent';
import {
  FEATURE_REGISTRY,
  getTierDisplayName,
  getTierPricing,
} from '../../config/subscriptionTiers';
import type { Feature, SubscriptionTier } from '../../config/subscriptionTiers';

interface OutletContextType {
  userData: any;
  companyData: any;
}

type TabId = 'access' | 'plans' | 'guides' | 'support';

const TABS: { id: TabId; label: string; icon: React.ComponentType<any>; color: string }[] = [
  { id: 'access', label: 'Your Access', icon: ShieldCheck, color: 'from-blue-500 to-cyan-500' },
  { id: 'plans', label: 'Plans & Features', icon: CreditCard, color: 'from-purple-500 to-pink-500' },
  { id: 'guides', label: 'Guides', icon: BookOpen, color: 'from-green-500 to-teal-500' },
  { id: 'support', label: 'Contact Support', icon: LifeBuoy, color: 'from-orange-500 to-red-500' },
];

const TIER_ORDER: SubscriptionTier[] = ['plus', 'pro', 'enterprise'];

const TIER_STYLES: Record<SubscriptionTier, { ring: string; badge: string; bg: string }> = {
  plus: {
    ring: 'ring-2 ring-cyan-500',
    badge: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    bg: 'from-cyan-500/10 to-transparent',
  },
  pro: {
    ring: 'ring-2 ring-purple-500',
    badge: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    bg: 'from-purple-500/10 to-transparent',
  },
  enterprise: {
    ring: 'ring-2 ring-amber-500',
    badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    bg: 'from-amber-500/10 to-transparent',
  },
  // Internal lateral tier (Elevate/lmg-network) — not in TIER_ORDER, so it
  // never renders as a plan card; entry exists for Record exhaustiveness and
  // the current-tier badge on the support tab.
  network: {
    ring: 'ring-2 ring-slate-500',
    badge: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    bg: 'from-slate-500/10 to-transparent',
  },
};

// ---------- Tab: Your Access ----------

function YourAccessTab({ role, userData }: { role: AppRole; userData: any }) {
  const roleInfo = ROLE_DESCRIPTIONS[role];

  const hasPermission = (perm: Permission): boolean => {
    return perm.roles.includes(role);
  };

  const getNote = (perm: Permission): string | undefined => {
    if (!perm.note) return undefined;
    // Only show broker-specific notes for brokers, admin notes for admins, etc.
    if (perm.note.toLowerCase().startsWith('broker:') && role !== 'broker') return undefined;
    if (perm.note.toLowerCase().startsWith('administrator:') && role !== 'administrator') return undefined;
    return perm.note;
  };

  return (
    <div className="space-y-6">
      {/* Role summary card */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-6 h-6 text-blue-400" />
          <h3 className="text-xl font-bold text-white">
            You are a <span className="text-blue-400">{roleInfo.title}</span>
          </h3>
        </div>
        <p className="text-gray-400 text-sm">{roleInfo.summary}</p>
        {role === 'administrator' && userData?.canSeeAllOffices && (
          <div className="mt-3 flex items-center gap-2 text-sm text-green-400">
            <CheckCircle className="w-4 h-4" />
            <span>canSeeAllOffices is enabled — you have company-wide data access</span>
          </div>
        )}
        {role === 'broker' && userData?.hideCommissions && (
          <div className="mt-3 flex items-center gap-2 text-sm text-amber-400">
            <Info className="w-4 h-4" />
            <span>Commission values are hidden on your account</span>
          </div>
        )}
      </div>

      {/* Permission categories */}
      {ROLE_PERMISSIONS.map((cat) => (
        <div key={cat.category} className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6">
          <h4 className="text-lg font-semibold text-white mb-4">{cat.category}</h4>
          <div className="space-y-3">
            {cat.permissions.map((perm) => {
              const granted = hasPermission(perm);
              const note = getNote(perm);
              return (
                <div key={perm.label} className="flex items-start gap-3">
                  {granted ? (
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  ) : (
                    <Lock className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={granted ? 'text-white' : 'text-gray-500'}>
                        {perm.label}
                      </span>
                      {perm.tier && (
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${TIER_STYLES[perm.tier].badge}`}>
                          {perm.tier.toUpperCase()}
                        </span>
                      )}
                    </div>
                    {note && granted && (
                      <p className="text-xs text-gray-500 mt-0.5">{note}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* FAQ for this role */}
      {FAQ_ENTRIES.filter(
        (faq) => !faq.roles || faq.roles.includes(role)
      ).length > 0 && (
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6">
          <h4 className="text-lg font-semibold text-white mb-4">Frequently Asked Questions</h4>
          <div className="space-y-4">
            {FAQ_ENTRIES.filter((faq) => !faq.roles || faq.roles.includes(role)).map((faq) => (
              <FaqAccordion key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Tab: Plans & Features ----------

function PlansTab({ currentTier, userRole }: { currentTier: SubscriptionTier; userRole: AppRole }) {
  // Group features by tier
  const featuresByTier = useMemo(() => {
    const grouped: Record<SubscriptionTier, { name: string; description: string }[]> = {
      plus: [],
      pro: [],
      enterprise: [],
      network: [], // internal tier — collected but never rendered (not in TIER_ORDER)
    };
    for (const [, config] of Object.entries(FEATURE_REGISTRY)) {
      grouped[config.requiredTier].push({
        name: config.name,
        description: config.description,
      });
    }
    return grouped;
  }, []);

  const currentTierIndex = TIER_ORDER.indexOf(currentTier);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIER_ORDER.map((tier, tierIndex) => {
          const isCurrent = tier === currentTier;
          const tierStyle = TIER_STYLES[tier];
          const isIncluded = tierIndex <= currentTierIndex;

          return (
            <div
              key={tier}
              className={`bg-gray-800/50 backdrop-blur-sm border rounded-2xl p-6 relative ${
                isCurrent ? `${tierStyle.ring} border-transparent` : 'border-gray-700'
              }`}
            >
              {isCurrent && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-bold rounded-full border ${tierStyle.badge}`}>
                  CURRENT PLAN
                </div>
              )}

              <div className={`text-center mb-6 ${isCurrent ? 'mt-2' : ''}`}>
                <h3 className="text-xl font-bold text-white mb-1">
                  {getTierDisplayName(tier)}
                </h3>
                <p className="text-2xl font-bold text-white">
                  {getTierPricing(tier)}
                </p>
              </div>

              {/* Entitlement limits */}
              <div className="space-y-2 mb-6 text-sm">
                {tier === 'plus' && (
                  <>
                    <div className="text-gray-400">1 loan book template</div>
                    <div className="text-gray-400">1 office</div>
                    <div className="text-gray-400">Up to 5 users</div>
                  </>
                )}
                {tier === 'pro' && (
                  <>
                    <div className="text-gray-400">Up to 3 templates</div>
                    <div className="text-gray-400">Up to 5 offices</div>
                    <div className="text-gray-400">Up to 30 users</div>
                  </>
                )}
                {tier === 'enterprise' && (
                  <>
                    <div className="text-gray-400">Unlimited templates</div>
                    <div className="text-gray-400">Unlimited offices</div>
                    <div className="text-gray-400">Unlimited users</div>
                  </>
                )}
              </div>

              {/* Feature list */}
              <div className="border-t border-gray-700 pt-4 space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  {tier === 'plus' ? 'Included features' : `Everything in ${getTierDisplayName(TIER_ORDER[tierIndex - 1])}, plus:`}
                </p>
                {featuresByTier[tier].map((f) => (
                  <div key={f.name} className="flex items-start gap-2">
                    <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isIncluded ? 'text-green-400' : 'text-gray-600'}`} />
                    <span className={`text-sm ${isIncluded ? 'text-gray-300' : 'text-gray-500'}`}>
                      {f.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Upgrade CTA */}
      {currentTier !== 'enterprise' && (
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 text-center">
          <h4 className="text-lg font-semibold text-white mb-2">Ready to upgrade?</h4>
          {userRole === 'super_admin' ? (
            <Link
              to="/settings/billing"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all duration-200"
            >
              Go to Billing
              <ExternalLink className="w-4 h-4" />
            </Link>
          ) : (
            <p className="text-gray-400 text-sm">
              Contact your Super Admin to upgrade your subscription plan.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Tab: Guides ----------

function GuidesTab({ role }: { role: AppRole }) {
  const filteredGuides = useMemo(
    () => GUIDES.filter((g) => !g.roles || g.roles.includes(role)),
    [role]
  );

  return (
    <div className="space-y-4">
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6">
        <h4 className="text-lg font-semibold text-white mb-4">Reference Guides</h4>
        <div className="space-y-3">
          {filteredGuides.map((guide) => (
            <GuideAccordion key={guide.title} title={guide.title} content={guide.content} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Tab: Contact Support ----------

function SupportTab({ currentTier, userRole }: { currentTier: SubscriptionTier; userRole: AppRole }) {
  return (
    <div className="space-y-6">
      {/* SLA banner */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className={`px-2.5 py-1 text-xs font-bold rounded border ${TIER_STYLES[currentTier].badge}`}>
            {getTierDisplayName(currentTier).toUpperCase()}
          </div>
          <span className="text-white font-semibold">
            {/* Internal 'network' tier has no commercial SLA — support goes direct. */}
            Your support SLA:{' '}
            {currentTier === 'network' ? 'Internal' : SUPPORT_CHANNELS[0].sla[currentTier]}
          </span>
        </div>
        <p className="text-gray-400 text-sm">
          Response times are based on your subscription tier during business hours (AEST).
        </p>
      </div>

      {/* Support channels */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {SUPPORT_CHANNELS.map((channel) => (
          <div
            key={channel.title}
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6"
          >
            <h4 className="text-lg font-semibold text-white mb-2">{channel.title}</h4>
            <p className="text-gray-400 text-sm mb-4">{channel.description}</p>
            <a
              href={`mailto:${channel.email}`}
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              <Mail className="w-4 h-4" />
              {channel.email}
            </a>
          </div>
        ))}
      </div>

      {/* Legal links */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6">
        <h4 className="text-lg font-semibold text-white mb-4">Legal</h4>
        <div className="flex flex-wrap gap-4">
          <a
            href={LEGAL_URLS.terms}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Terms of Service
          </a>
          <a
            href={LEGAL_URLS.privacy}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------- Shared Components ----------

function FaqAccordion({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-white hover:bg-gray-700/30 transition-colors"
      >
        <span>{question}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 text-sm text-gray-400 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

function GuideAccordion({ title, content }: { title: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-white hover:bg-gray-700/30 transition-colors"
      >
        <span>{title}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 text-sm text-gray-400 leading-relaxed">
          {content}
        </div>
      )}
    </div>
  );
}

// ---------- Main Page ----------

const HelpPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<OutletContextType>();
  const { tier } = useSubscriptionTier();
  const [activeTab, setActiveTab] = useState<TabId>('access');

  const userRole: AppRole = userData?.role || 'broker';

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <PageHeader
          icon={HelpCircle}
          title="Help & Support"
          subtitle="Understand your access, explore plans, and get help"
        />

        {/* Tab Navigation */}
        <div className="flex space-x-2 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 flex-shrink-0 ${
                  activeTab === tab.id
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-lg transform scale-105`
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'access' && <YourAccessTab role={userRole} userData={userData} />}
        {activeTab === 'plans' && <PlansTab currentTier={tier} userRole={userRole} />}
        {activeTab === 'guides' && <GuidesTab role={userRole} />}
        {activeTab === 'support' && <SupportTab currentTier={tier} userRole={userRole} />}
      </div>
    </div>
  );
};

export default HelpPage;
